<template>
  <AppDialogContent
    v-model="dialogValue"
    :loading="formSubmitted"
    :persistent="formSubmitted"
    custom-header
    @submit-form="submit"
  >
    <template #header>
      <div class="d-flex justify-space-between py-4">
        <div v-if="!!responseType" style="align-self: center">
          <div class="text-h5 font-weight-bold">
            {{ responseTexts.headline }}
          </div>
        </div>
        <div v-else style="align-self: center">
          <div class="text-h5 font-weight-bold mb-2">
            {{
              $t("events:entryAction.applyFee.heading", "Apply fee for entry")
            }}
          </div>
          <p>
            {{
              $t(
                "events:entryAction.applyFee.explanation.text",
                "You are going to apply fee to the selected entry."
              )
            }}
          </p>
        </div>
        <div class="float-end">
          <v-btn-icon
            icon
            :disabled="disable || formSubmitted"
            class="mx-0"
            variant="text"
            data-test="closeDialog"
            :aria-label="$t('common:closeButton.alt')"
            @click="() => (dialogValue = false)"
          >
            <v-icon>$iconTimesCircle</v-icon>
          </v-btn-icon>
        </div>
      </div>
    </template>

    <template #default>
      <div v-if="!!responseType">
        <EntryFeiResponse
          :response-type="responseType"
          :mutation-errors="mutationErrors"
        >
          <template #default>
            {{ responseTexts.main }}
          </template>
          <template #reason>
            <div v-if="responseType == 'error'">
              {{ responseTexts.reason }}<br />
            </div>
          </template>
          <template #additional-message>
            {{ responseTexts.additional }}
          </template>
        </EntryFeiResponse>
      </div>
      <div v-else-if="entryLoading">
        <v-progress-linear indeterminate />
      </div>
      <div v-else-if="entry">
        <AppShowEventDetails v-if="event" :event="event" show-logo />

        <br />

        <AppEntryRegistrationOverview v-bind="{ registration, competition }">
          <template #competitionCodeheader>
            {{
              $t("events:entryAction.applyFee.competitionCode.label", "Entry")
            }}
          </template>
          <template #athleteNamesHeader>
            {{
              $t("events:entryAction.applyFee.athleteNames.label", "Athlete")
            }}
          </template>
          <template #horseNamesHeader>
            {{ $t("events:entryAction.applyFee.horseNames.label", "Horse") }}
          </template>
        </AppEntryRegistrationOverview>

        <br />

        <v-progress-linear v-if="initFormLoading" indeterminate class="mb-5" />

        <AppBillingContact
          v-if="v$.form.billingContact"
          ref="billingContactForm"
          data-test="billingContactFeiOrderPayable"
          contactable-type="Entry"
          :contact-object="billingContact"
          :contact-id="billingContact ? billingContact.id : undefined"
          :person-id="registration.registrantPersonId"
          :contactable-id="entry.id"
          :contact-type="
            billingContact ? billingContact.contactType : undefined
          "
          :force-cols="12"
          :disabled="disable || fieldDisabled('billingContact')"
          @set-contact="contact => (billingContact = contact)"
        />

        <div class="mb-4">
          <strong>{{
            $t(
              "events:entryAction.applyFee.specifyProduct.subheading",
              "Specify products or fee type"
            )
          }}</strong>
        </div>

        <v-select
          v-if="v$.form.productId"
          v-model="form.product"
          return-object
          item-title="label"
          item-value="value"
          :items="products"
          :label="
            $t(
              'events:entryAction.applyFee.productId.label',
              'Select product or fee type from the list'
            )
          "
          data-test="productId"
          variant="outlined"
          :error-messages="fieldErrorMessages('productId')"
          :disabled="fieldDisabled('productId')"
          @update:model-value="product => (form.productId = product.value)"
          @blur="() => v$.form.productId.$touch()"
        />

        <v-text-field
          v-if="v$.form.note"
          v-model="form.note"
          :label="$t('events:entryAction.applyFee.note.label', 'Description')"
          :hint="
            $t(
              'events:entryAction.applyFee.note.hint',
              'Will be shown on invoice or receipt'
            )
          "
          data-test="note"
          persistent-hint
          variant="outlined"
          :error-messages="fieldErrorMessages('note')"
          :disabled="fieldDisabled('note')"
          @blur="() => v$.form.note.$touch()"
        />

        <br />

        <div class="mb-2">
          <b>{{
            $t(
              "events:entryAction.applyFee.alertNote.heading",
              "Please read before submitting:"
            )
          }}</b>
        </div>

        <AppAlertNotice center>
          {{
            $t(
              "events:entryAction.applyFee.alertNote.text",
              "Based on your selection, a payable for a fee will be created and automatically added either to existing draft invoice of the same type for the same billing contact or a new draft invoice will be created. Go to Invoices Backoffice interface to check the invoice."
            )
          }}
        </AppAlertNotice>
      </div>
    </template>

    <template #submit>
      {{
        $t("events:entryAction.applyFee.confirm.button", "Confirm apply fee")
      }}
    </template>

    <template v-if="!!responseType" #actions>
      <v-row dense style="justify-content: flex-end">
        <div class="mb-1 text-center text-md-right">
          <v-btn
            rounded
            variant="outlined"
            min-width="180px"
            data-test="dialogClose"
            @click="() => (dialogValue = false)"
          >
            {{ responseTexts.okBtn }}
          </v-btn>
        </div>
      </v-row>
    </template>
  </AppDialogContent>
</template>
<script setup>
import { ref, computed, useTemplateRef } from "vue";
import gql from "fraql";
import { apolloClient } from "@/plugins/apollo";
import { DisplayTextFragments } from "@composables/display_text";
import { useFormValidation } from "@/composables/form/form_validation";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppShowEventDetails from "@AppShows/event/AppShowEventDetails.vue";
import { useEntryFeiActionsInjections } from "./compositions";
import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
import EntryFeiResponse from "./EntryFeiResponse.vue";
import AppEntryRegistrationOverview from "@elements/AppEntryRegistrationOverview.vue";
import AppBillingContact, {
  prepContactForBillingInput
} from "@elements/billing/AppBillingContact.vue";
import { injectSnackbarError } from "@/composables/injections";
import { useTranslation } from "i18next-vue";
import { useQuery } from "@vue/apollo-composable";
//import AppShowRegistrationHorses from "@AppShows/horse/AppShowRegistrationHorses.vue";
//import AppShowRegistrationPeople from "@AppShows/person/AppShowRegistrationPeople.vue";

defineOptions({
  name: "EntryFeiOrderPayable"
});

const { fragments, refetchQueries } = useEntryFeiActionsInjections();
const { snackbarServerError } = injectSnackbarError();
const { t } = useTranslation();

const form = ref({
  entryId: null,
  productId: null,
  product: null,
  note: null
});
const responseType = ref(null);
const mutationErrors = ref([]);
const feedbackMessages = ref([]);

const billingContact = ref(null);
const billingContactForm = useTemplateRef("billingContactForm");

const loading = ref(false);
const formSubmitted = ref(false);
const formError = ref(null);
const initEntryOrderProductForm = ref({
  formFields: [],
  vuelidateValidations: {},
  readOnlyFields: []
});

const {
  v$,
  fieldErrorMessages,
  fieldDisabled,
  formValidate,
  formErrorMessages
} = useFormValidation(initEntryOrderProductForm, form);

const dialog = ref(false);
const dialogValue = computed({
  get() {
    return dialog.value;
  },
  set(v) {
    if (v == true && !dialog.value) {
      billingContact.value = null;
      form.value = {
        entryId: null,
        productId: null,
        product: null,
        note: null
      };
      feedbackMessages.value = [];
      responseType.value = null;
      mutationErrors.value = [];
    }
    dialog.value = v;
  }
});

const entryLoading = ref(false);
const entry = ref(null);

const registration = computed(() => {
  return entry.value?.registration;
});
const event = computed(() => {
  return entry.value?.registration?.event;
});
const competition = computed(() => {
  return entry.value?.competition;
});

const disable = computed(() => {
  return initFormLoading.value || loading.value || formSubmitted.value;
});

const products = computed(() => {
  return (
    initEntryOrderProductForm.value?.formFields?.find(
      x => x.name == "productId"
    )?.possibleValues ?? []
  );
});
const responseTexts = computed(() => {
  switch (responseType.value) {
    case "success":
      return {
        headline: t(
          "events:entryAction.applyFee.success.heading",
          "Adding fee to entry"
        ),
        main: t(
          "events:entryAction.applyFee.success.mainMessage.text",
          "Fee is created and saved"
        ),
        okBtn: t("events:entryAction.applyFee.success.ok.button", "OK")
      };
    case "error":
      return {
        headline: t(
          "events:entryAction.applyFee.failed.heading",
          "Adding fee to entry failed"
        ),
        main: t(
          "events:entryAction.applyFee.failed.mainMessage.text",
          "Action failed: the fee cannot be created"
        ),
        additional: t(
          "events:entryAction.applyFee.failed.additionalMessage.text",
          "Please contact technical support, if you need help on this matter."
        ),
        okBtn: t("events:entryAction.applyFee.failed.ok.button", "OK")
      };
    default:
      return null;
  }
});

const { onResult, loading: initFormLoading } = useQuery(
  gql`
    query initEntryOrderProductForm(
      $entryId: ID!
      $productId: ID
    ) {
      initEntryOrderProductForm(
        entryId: $entryId
        productId: $productId
      ) {
        entry {
          id
          billingContact {
            id
            ${AppBillingContact.fragments.contactFields}
          }
        }
        formFields {
          name
          possibleValues
        }
        vuelidateValidations
        readOnlyFields
      }
    }
  `,
  () => ({
    entryId: entry.value.id,
    productId: form.value.productId
  }),
  () => ({
    enabled: dialogValue.value == true && !entryLoading.value
  })
);

onResult(({ loading, data }) => {
  if (loading || !data) return;
  initEntryOrderProductForm.value = data.initEntryOrderProductForm;
  form.value.entryId = entry.value.id;
  billingContact.value = data.initEntryOrderProductForm.entry.billingContact;
});

async function openFeiActionForm({ entry: e }) {
  entryLoading.value = true;
  dialogValue.value = true;
  try {
    const { data } = await apolloClient.query({
      query: gql`
            query EntryFeiOrderPayableFetch($id: ID!) {
              entry(id: $id) {
                id
                allowedMutations
                registration {
                  id
                  registrantPersonId
                  athletes {
                    id
                    feiid
                    identifier
                    name
                  }
                  registrationPeople {
                    id
                    personId
                  }

                  horses {
                    id
                    feiid
                    identifier
                    name
                  }
                  registrationHorses {
                    id
                    horseId
                  }
                  eventId
                  event {
                    id
                    place
                    startDate
                    ${AppShowEventDetails.fragments.event}
                  }
                }
                competition {
                  id
                  code
                  ${AppEntryRegistrationOverview.fragments.competitionFields}
                }
              }
            }
          `,
      variables: {
        id: e.id
      },
      fetchPolicy: "network-only"
    });
    entry.value = data.entry;
  } catch (error) {
    snackbarServerError(error, { level: "error" });
  } finally {
    entryLoading.value = false;
  }
}

async function submit() {
  formError.value = null;
  if (!formValidate()) {
    formError.value = formErrorMessages();
    return;
  }
  if (billingContactForm.value) {
    const contactData = billingContactForm.value.validateContactFormManually({
      returnContactAndCloseForm: true
    });
    if (contactData == false) return;
    else if (typeof contactData === "object") {
      billingContact.value = contactData;
    }
  }
  formSubmitted.value = true;

  try {
    const { data, errors } = await apolloClient.mutate({
      mutation: gql`
        mutation entryOrderProduct(
          $entryId: ID!
          $productId: ID!
          $note: String
          $billingContact: ContactForBillingInput
        ) {
          entryOrderProduct(
            input: {
              entryId: $entryId
              productId: $productId
              note: $note
              billingContact: $billingContact
            }
          ) {
            feedbackMessages {
              ${DisplayTextFragments.translation}
            }
            entry {
              id
              status
              resolution
              allowedMutations
            }
          }
        }
      `,
      variables: {
        entryId: entry.value.id,
        productId: form.value.productId,
        note: form.value.note,
        billingContact: prepContactForBillingInput(billingContact.value)
      }
    });

    if (data?.entryOrderProduct) {
      responseType.value = "success";
      feedbackMessages.value = data.entryOrderProduct.feedbackMessages ?? [];
    } else if (errors && errors.length > 0) {
      responseType.value = "error";
      mutationErrors.value = errors;
    }
  } catch (error) {
    responseType.value = "error";
    if (error?.graphQLErrors) {
      mutationErrors.value = error.graphQLErrors;
    }
  } finally {
    formSubmitted.value = false;
  }
}

defineExpose({ openFeiActionForm });
</script>
